import type { ReactNode } from "react";
import { VscQuestion } from "react-icons/vsc";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import TextCopy from "../../../../../components/common/TextCopy";
import Tooltip from "../../../../../components/common/Tooltip";
import type { ConnectionHubError } from "../../../api/connectionHub.types";
import type { ProviderSetupGuide } from "../../../constants/providerSetupGuides.constants";
import ConnectionErrorPanel from "../../common/ConnectionErrorPanel/ConnectionErrorPanel";
import StepsAccordion from "../StepsAccordion/StepsAccordion";
import styles from "./ForwardingOnlyPanel.module.scss";

const ADDRESS_CAPTION = "Merito's official, verified address, used only to receive OTPs for this connection.";

export interface ForwardingOnlyPanelProps {
  mode?: "setup" | "recheck";
  forwardingAddress: string | null;
  filterFrom?: string | null;
  filterHasWords?: string | null;
  guide?: ProviderSetupGuide;
  error?: ConnectionHubError | string | null;
  appearance?: "default" | "primary";
  leading?: ReactNode;
  canConfirm?: boolean;
  disabled?: boolean;
  busy?: boolean;
  onConfirm: () => void;
}

const ForwardingOnlyPanel = ({
  mode = "setup",
  forwardingAddress,
  filterFrom,
  filterHasWords,
  guide,
  error,
  appearance = "default",
  leading,
  canConfirm = true,
  disabled = false,
  busy = false,
  onConfirm,
}: ForwardingOnlyPanelProps): JSX.Element => {
  const primary = appearance === "primary";
  const recheck = mode === "recheck";
  const conditions = [
    { label: "From:", value: filterFrom },
    { label: "Has words:", value: filterHasWords },
    ...(recheck ? [{ label: "Forwarding to:", value: forwardingAddress }] : []),
  ].filter((condition): condition is { label: string; value: string } => Boolean(condition.value));

  const confirmButton = (
    <MeritoButton
      type={primary ? "primary-1" : "secondary-2"}
      size="md"
      className={styles.action}
      btnText="I’ve Set Up Forwarding"
      loading={busy}
      showLoadingWithText
      disabled={disabled || !canConfirm}
      {...(primary ? { isRounded: true } : {})}
      onClick={onConfirm}
    />
  );

  return (
    <div className={styles.panel}>
      {error ? <ConnectionErrorPanel error={error} /> : null}
      <div className={styles.instruction}>
        {recheck ? (
          <p className={styles.sentence}>Check that forwarding and the filter conditions below are still correct for this inbox.</p>
        ) : (
          <p className={styles.sentence}>
            <span>Set up forwarding to</span>
            {forwardingAddress ? (
              <TextCopy iconOnlyFeedback text={forwardingAddress} ariaLabel="Copy Merito forwarding address" className={styles.chip} />
            ) : null}
            <Tooltip content={ADDRESS_CAPTION} direction="top">
              <VscQuestion className={styles.info} aria-label={ADDRESS_CAPTION} />
            </Tooltip>
            <span>and the filter conditions below for this inbox.</span>
          </p>
        )}
        {conditions.map((condition) => (
          <p className={styles.condition} key={condition.label}>
            <span>{condition.label}</span>
            <TextCopy iconOnlyFeedback text={condition.value} ariaLabel={`Copy ${condition.label}`} className={styles.chip} />
          </p>
        ))}
      </div>
      {!recheck && guide?.forwarding.length ? (
        <StepsAccordion
          steps={guide.forwarding}
          videoUrl={guide.forwardingVideoUrl}
          phase="forwarding"
          providerName={guide.displayName}
          forwardingAddress={forwardingAddress}
        />
      ) : null}
      {leading ? (
        <div className={styles.leadingRow}>
          {leading}
          {confirmButton}
        </div>
      ) : (
        <div className={styles.actions}>{confirmButton}</div>
      )}
    </div>
  );
};

export default ForwardingOnlyPanel;
